import { TableColumnType, TableFilterType } from "types/baTypes";
import { CUSTOM_SEARCH_FILTERS_PARAMS_SET, FILTERS_PARAMS_SET, RecordItem } from "types/common";
import { CellType, FILTER_OPERATOR } from "./constants";

export const getColumnSearchParamName = (col: TableFilterType | TableColumnType) =>
  "f_" + (col.header?.toLowerCase().replace(" ", "_") || "");

export const getCustomSearchFilterParamName = (name?: string) => "csf_" + name?.toLowerCase().replace(" ", "_");

/**
 * Used to set/remove query params in the url using useQueryParams hook
 */
export enum APP_QUERY_PARAM_TYPES {
  FILTERS = "filters",
  SORT = "sort",
  CUSTOM_SEARCH_FILTERS = "customSearchFilters",
  SIDEBAR = "sidebar",
  SEARCH_QUERY = "searchQuery",
  FORM = "form",
  EXPANDED = "expanded",
  CELL_SIDEBAR = "cellSidebar",
  VIEW_SECTION = "viewSection",
  COLLAPSIBLE_VIEWER = "collapsibleViewer",
  DEAL_QUICK_EXPAND = "dealQuickExpand",
  DEAL_QUICK_EXPAND_TAB = "dealQuickExpandTab"
}

/**
 * This is to be updated when adding a new query param to our app
 * add it to QUERY_PARAMS_INITIAL_STATE in QueryParamsContext.tsx so that it can be parsed by the app
 * */
export enum APP_QUERY_PARAMS_KEYS {
  FILTERS = "filters",
  FILTERS_SLUG = "filters_slug",
  SORT = "sort",
  SORT_SLUG = "sort_slug",
  CUSTOM_SEARCH_FILTERS = "custom_search_filters",
  CUSTOM_SEARCH_FILTERS_SLUG = "custom_search_filters_slug",
  SIDE_PAGE = "side_page",
  SIDE_RECORD = "side_record",
  SIDE_FILE = "side_file",
  FORM_ID = "form_id",
  FORM_PAGE_FORM_ID = "form_page_form_id",
  FORM_PAGE_ID = "form_page_id",
  FORM_PATH = "form_path",
  FORM_SIDEBAR = "form_sidebar",
  FORM_ACTION = "form_action",
  FORM_ADD = "form_add",
  FORM_ADD_TAB = "form_add_tab",
  FORM_PARENTID = "form_parentid",
  FORM_EXPANDED = "form_expanded",
  FORM_PARENT_PAGE_ID = "form_parent_page_id",
  FORM_TITLE = "form_title",
  FORM_TRIGGER = "form_trigger",
  VIEW_SECTION = "view_section",
  VIEW_SECTION_SLUG = "view_sectionslug",
  EXPANDED = "expanded",
  CELL_SIDE_PAGE_ID = "cell_sidepageid",
  CELL_SIDE_RECORD_ID = "cell_siderecordid",
  CELL_SIDE_FILE = "cell_sidefile",
  CELL_TAB_ID = "cell_tabid",
  CELL_SIDE_TYPE = "cell_sidetype",
  CELL_SIDE_TITLE = "cell_sidetitle",
  COLLAPSIBLE_VIEWER_REMOVE_ITEM = "collapsible_viewer_remove_item",
  SEARCH = "search",
  DEAL_QUICK_EXPAND_RECORD = "deal_quick_expand_record",
  DEAL_QUICK_EXPAND_TAB_PATH = "deal_quick_expand_tab_path",
  DEAL_QUICK_EXPAND_TAB = "deal_quick_expand_tab"
}

export const getFilterParams = (filter: FILTERS_PARAMS_SET) => {
  const filterParams: RecordItem = {};
  let filterName = getColumnSearchParamName(filter.column);
  if (filter.filterOperator) {
    filterName = `${filterName}:${filter.filterOperator}`;
  }
  const isColTextArray = !!filter?.column?.isTextArray;
  let filterValue;
  if (Array.isArray(filter.filterValue) && filter.filterValue.length) {
    filterValue = isColTextArray
      ? filter.filterValue
      : filter.filterOperator === FILTER_OPERATOR.IN
        ? filter.filterValue.map((fItem: RecordItem) => fItem?.record?.id || fItem)
        : (filter.filterValue as RecordItem[])?.[0]?.record?.id;
    filterValue = filterValue?.join(",");
  } else {
    filterValue = (filter.filterValue as RecordItem)?.record?.id || filter.filterValue;
  }
  if (filter.filterOperator === FILTER_OPERATOR.EMPTY || filter.filterOperator === FILTER_OPERATOR.NOT_EMPTY) {
    filterParams[filterName] = "null";
  } else {
    filterParams[filterName] = filter.column?.type !== CellType.BOOLEAN ? filterValue || "" : filterValue;
  }
  return filterParams;
};

export const getCustomSearchFilterParams = (filter: CUSTOM_SEARCH_FILTERS_PARAMS_SET) => {
  const filterParams: RecordItem = {};
  const filterName = getCustomSearchFilterParamName(filter.filterName);
  let filterValue = filter.filterValue;
  if (Array.isArray(filterValue) && filterValue.length) {
    filterValue = filterValue.join(",");
  }

  if (filter.filterOperator === FILTER_OPERATOR.EMPTY || filter.filterOperator === FILTER_OPERATOR.NOT_EMPTY) {
    filterParams[filterName] = "null";
  } else {
    filterParams[filterName] = filterValue;
  }
  return filterParams;
};
